// A private note about who you think is sitting in a seat. Visible only to whoever wrote it —
// not to the other players, and not to a Storyteller either. The server keeps guesses keyed by
// their author and only ever puts your own into your projection, so this is private by
// construction rather than by the UI hiding it.

import { useState } from "react";
import { TEAM_ORDER } from "@cc/shared";
import type { GameView } from "@cc/shared";
import { useGame } from "../../state/game.js";
import { cn, teamColor, teamLabel } from "../../lib/ui.js";
import { Button, Dialog, DialogContent, Input } from "../ui/primitives.js";

export function GuessDialog({
  view,
  seatId,
  onClose,
}: {
  view: GameView;
  seatId: string | null;
  onClose: () => void;
}) {
  const send = useGame((s) => s.send);
  const [filter, setFilter] = useState("");
  const seat = view.seats.find((s) => s.id === seatId) ?? null;
  if (!seat) return null;

  const matches = view.script.characters.filter((c) =>
    filter ? c.name.toLowerCase().includes(filter.toLowerCase()) : true,
  );

  return (
    <Dialog open={seatId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={`Your guess for ${seat.displayName}`}
        description="Only you can see this — not the other players, not the Storyteller."
        className="w-[min(36rem,calc(100vw-2rem))]"
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter…" />
            {seat.myGuess && (
              <Button
                variant="ghost"
                onClick={() => {
                  void send("player:guess", { seatId: seat.id, characterId: null });
                  onClose();
                }}
              >
                Clear
              </Button>
            )}
          </div>

          {view.script.characters.length === 0 ? (
            <p className="text-xs text-parchment-dim">No script loaded yet.</p>
          ) : (
            <div className="max-h-72 overflow-y-auto cc-scroll">
              {TEAM_ORDER.filter((team) => matches.some((c) => c.team === team)).map((team) => (
                <div key={team} className="mb-2">
                  <h4
                    className="mb-1 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: teamColor(team) }}
                  >
                    {teamLabel(team)}
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {matches
                      .filter((c) => c.team === team)
                      .map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            void send("player:guess", { seatId: seat.id, characterId: c.id });
                            onClose();
                          }}
                          className={cn(
                            "flex items-center gap-1 rounded border px-1.5 py-1 text-[11px] transition",
                            seat.myGuess === c.id
                              ? "border-brass bg-brass/20 text-parchment"
                              : "border-ink-line text-parchment-dim hover:text-parchment",
                          )}
                        >
                          {c.icon && <img src={c.icon} alt="" className="size-5 object-contain" />}
                          {c.name}
                        </button>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
